import { useEffect, useState } from "react"
import { X, Check, CheckCheck } from "lucide-react"
import type { BroadcastMessageReceipts } from "@/sdk/instavcMessenger"
import { fetchBroadcastReceipt } from "@/sdk/instavcMessenger-instance"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { AvatarPhoto } from "./avatar-photo"

/**
 * "Message info" for a broadcast message — who has it, who has read it.
 *
 * The per-recipient view behind the aggregate tick: each row is one recipient's
 * copy in their own DM, so `readAt` is when THEY opened your message, not when
 * anyone opened the list.
 */
interface BroadcastMessageInfoProps {
  broadcastId: string
  messageId: string
  messageText?: string
  onClose: () => void
}

function timeOf(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
}

export function BroadcastMessageInfo({ broadcastId, messageId, messageText, onClose }: BroadcastMessageInfoProps) {
  const myUserId = useAuthStore((s) => s.user?.id)
  const [receipt, setReceipt] = useState<BroadcastMessageReceipts | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!myUserId) return
    let cancelled = false
    fetchBroadcastReceipt(myUserId, broadcastId, messageId)
      .then((r) => { if (!cancelled) setReceipt(r) })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : "failed to load") })
    return () => { cancelled = true }
  }, [myUserId, broadcastId, messageId])

  const recipients = receipt?.recipients ?? []
  const readRows = recipients.filter((r) => r.readAt)
  const deliveredRows = recipients.filter((r) => !r.readAt && r.deliveredAt)
  const pendingRows = recipients.filter((r) => !r.readAt && !r.deliveredAt)

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-base font-semibold text-foreground">Message info</h3>
        <button onClick={onClose} className="p-2 rounded-full hover:bg-muted text-muted-foreground" title="Close">
          <X className="w-4.5 h-4.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {messageText && (
          <div className="mx-4 mt-4 mb-3 rounded-2xl bg-primary/10 px-4 py-3">
            <p className="text-sm text-foreground break-words">{messageText}</p>
          </div>
        )}

        {error ? (
          <p className="px-4 py-6 text-xs text-muted-foreground text-center">{error}</p>
        ) : !receipt ? (
          <p className="px-4 py-6 text-xs text-muted-foreground text-center">Loading…</p>
        ) : receipt.total === 0 ? (
          <p className="px-4 py-6 text-xs text-muted-foreground text-center">
            No delivery record for this message — it was sent before receipts were available.
          </p>
        ) : (
          <>
            <div className="mx-4 mb-3 flex gap-2">
              <div className="flex-1 rounded-xl border border-border bg-card px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Read</p>
                <p className="text-sm font-semibold text-foreground">{receipt.read} of {receipt.total}</p>
              </div>
              <div className="flex-1 rounded-xl border border-border bg-card px-3 py-2">
                <p className="text-[11px] text-muted-foreground">Delivered</p>
                <p className="text-sm font-semibold text-foreground">{receipt.delivered} of {receipt.total}</p>
              </div>
            </div>

            <Section title="Read by" icon={<CheckCheck className="w-4 h-4 text-primary" />} rows={readRows} stamp={(r) => timeOf(r.readAt)} />
            <Section title="Delivered to" icon={<CheckCheck className="w-4 h-4 text-muted-foreground" />} rows={deliveredRows} stamp={(r) => timeOf(r.deliveredAt)} />
            <Section title="Pending" icon={<Check className="w-4 h-4 text-muted-foreground" />} rows={pendingRows} stamp={() => ""} />
          </>
        )}
      </div>
    </div>
  )
}

function Section({
  title,
  icon,
  rows,
  stamp,
}: {
  title: string
  icon: React.ReactNode
  rows: BroadcastMessageReceipts["recipients"] extends (infer R)[] | undefined ? R[] : never
  stamp: (r: { deliveredAt: string | null; readAt: string | null }) => string
}) {
  if (rows.length === 0) return null
  return (
    <div className="mx-4 mb-3 rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border">
        {icon}
        <p className="text-sm font-medium text-foreground">{title} ({rows.length})</p>
      </div>
      {rows.map((r) => (
        <div key={r.userId} className="flex items-center gap-3 px-4 py-2.5">
          <div className="w-8 h-8 rounded-full bg-muted overflow-hidden shrink-0">
            <AvatarPhoto
              src={r.avatarUrl}
              alt={r.name ?? ""}
              className="w-full h-full object-cover"
              fallback={
                <div className="w-full h-full flex items-center justify-center text-xs font-medium text-muted-foreground">
                  {(r.name ?? "?")[0]?.toUpperCase()}
                </div>
              }
              contactId={r.userId}
            />
          </div>
          <span className="flex-1 text-sm text-foreground truncate">{r.name ?? `User ${r.userId.slice(0, 8)}`}</span>
          <span className="text-[11px] text-muted-foreground shrink-0">{stamp(r)}</span>
        </div>
      ))}
    </div>
  )
}
